/**
 * @fileoverview A sky's icon key, as the server sends it, drawn as the app's standard Lucide icon of that name (W24 Job D:
 * the server decides the sky; owner, 2026-10-09: the standard icons everywhere, not the forecast's own sketches). It holds the
 * closed list of ten keys and nothing else: it draws the key it is sent and matches no words. A key it does not know, or
 * none, draws nothing.
 *
 * Kept in code on purpose: the ten keys and their Lucide components are the app's drawing (a new key needs a new icon anyway).
 *
 * Where: the header's WeatherPill, Today's weather card and Coming up's days, the Plants page's GardenConditionsBar, the
 * Weather page's current conditions and bare week, and ForecastWeek's rows (<SkyIcon sky="cloud-sun" />).
 * API: none.
 *
 * @module SkyIcon
 */

import {
  Cloud, CloudDrizzle, CloudFog, CloudLightning, CloudRain, CloudSun, Moon, Snowflake, Sun, Wind, type LucideIcon, type LucideProps,
} from 'lucide-react';
import type { SkyKey } from '@/shared/api/header';

/** Each sky key's Lucide icon (the closed list of ten). */
const ICONS: Record<SkyKey, LucideIcon> = {
  'sun': Sun,
  'moon': Moon,
  'cloud-sun': CloudSun,
  'cloud': Cloud,
  'cloud-rain': CloudRain,
  'cloud-drizzle': CloudDrizzle,
  'cloud-lightning': CloudLightning,
  'cloud-fog': CloudFog,
  'snowflake': Snowflake,
  'wind': Wind,
};

/** What SkyIcon takes: the key, and what any Lucide icon takes (its size, stroke, aria). */
export type SkyIconProps = Omit<LucideProps, 'name'> & {
  /** The server's sky key; null or undefined draws nothing. */
  sky: SkyKey | null | undefined;
};

/**
 * The sky's icon for a key.
 *
 * @param props.sky - The server's sky key.
 * @returns The Lucide icon, or nothing for no key (or one outside the list).
 */
export function SkyIcon({ sky, ...rest }: SkyIconProps): JSX.Element | null {
  const Icon = sky ? ICONS[sky] : undefined;
  return Icon ? <Icon {...rest} /> : null;
}
